<script setup>
import { ref, computed, watch, nextTick, onMounted } from 'vue';
import { useContentTagsStore } from '@/stores/content-tags';
import { useAuthStore } from '@/stores/auth';
import TagsBase from '@/app/library/components/MediaPopup/TagsBase.vue';
import Chip from '@/components/foundation/Chip.vue';
import ImageDropdownSelect from '@/components/foundation/form/ImageDropdownSelect.vue';

const authStore = useAuthStore();
const contentTagsStore = useContentTagsStore();

const emit = defineEmits(['add', 'remove']);

const props = defineProps({
  brandIds: {
    type: Array,
    default: () => [],
  },
  mediaContentTags: {
    type: Array,
    default: () => [],
  },
  saving: {
    type: Boolean,
    default: false,
  },
  openAddOnMount: {
    type: Boolean,
    default: false,
  },
  allowUpdates: {
    type: Boolean,
    default: true,
  },
});

const showAddInput = ref(false);
const contentTagToAdd = ref(null);
const addContentTagInput = ref(null);

const brandIds = computed(() =>
  props.brandIds?.length > 0 ? props.brandIds : [authStore.currentBrand?.id],
);

const availableContentTags = computed(
  () =>
    contentTagsStore.organizationContentTags
      ?.filter((organizationContentTag) =>
        organizationContentTag.brandIds.some((contentTagBrandId) =>
          brandIds.value.includes(contentTagBrandId),
        ),
      )
      ?.filter(
        (organizationContentTag) =>
          !props.mediaContentTags.some(
            (mediaContentTag) => mediaContentTag.id === organizationContentTag.id,
          ),
      )
      ?.map((contentTag) => ({
        text: contentTag.name,
        value: contentTag.id,
      })) ?? [],
);
const hasAvailableContentTags = computed(() => availableContentTags.value.length > 0);
const deleting = computed(() =>
  props.mediaContentTags?.some((mediaContentTag) => mediaContentTag.deleting),
);
const hasTags = computed(() => props.mediaContentTags?.length > 0);

function onAddTagClick() {
  showAddInput.value = true;
  nextTick(() => {
    addContentTagInput.value.showOptions();
  });
}

function onOptionsClose() {
  if (!contentTagToAdd.value) {
    showAddInput.value = false;
  }
}

async function onAddContentTag(option) {
  if (option) {
    const contentTag = contentTagsStore.organizationContentTags.find(
      (organizationContentTag) => organizationContentTag.id === option.value,
    );
    emit('add', contentTag);
  }
}

async function onRemoveTagClick(contentTagToRemove) {
  emit('remove', contentTagToRemove);
}

watch(
  () => ({
    contentTagToAdd,
    saving: props.saving,
  }),
  () => {
    if (!contentTagToAdd.value && !props.saving) {
      contentTagToAdd.value = null;
      if (hasAvailableContentTags.value) {
        onAddTagClick();
      } else {
        onOptionsClose();
      }
    }
  },
);

onMounted(() => {
  if (props.openAddOnMount) {
    onAddTagClick();
  }
});

// for unit tests
defineExpose({
  availableContentTags,
  showAddInput,
  contentTagToAdd,
});
</script>

<template>
  <TagsBase
    :tags="mediaContentTags"
    :prevent-actions="props.saving || deleting"
    :show-action-buttons="props.allowUpdates"
    @add="onAddTagClick"
    @remove="onRemoveTagClick"
  >
    <template #prependTags>
      <template v-if="props.allowUpdates && !hasTags && !showAddInput">
        <span class="text-small">Add</span>
      </template>
      <template v-else>
        <Chip v-if="showAddInput" color="var(--background-500)" small :loading-right="props.saving">
          <ImageDropdownSelect
            ref="addContentTagInput"
            v-model="contentTagToAdd"
            :options="availableContentTags"
            autocomplete
            return-object
            invisible
            min-picker-width="4rem"
            no-data-text="No content tags found."
            data-cy="AddContentTagDropdown"
            @update:model-value="onAddContentTag"
            @closed="onOptionsClose"
          />
        </Chip>
      </template>
    </template>
  </TagsBase>
</template>
